import { WorkflowDTO } from '@invenco/common-interface/automation';

import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';

/**
 * Fetch + update + delete for an existing workflow. `id` is optional so the
 * page can call this unconditionally: without one the fetch is disabled and
 * the mutations are never invoked.
 */
export function useWorkflow(id?: string) {
  const { automationGateway } = useGateways();
  const workflowQueryKey = ['workflows', id];

  const { data: workflow, isLoading } = useEntityDetailsQuery<WorkflowDTO>({
    parentKey: 'workflows',
    id,
    query: (fetchId, { signal }) => automationGateway.getWorkflow(fetchId, { signal }),
  });

  const { mutate: updateWorkflow } = useGatewayMutation<WorkflowDTO, Partial<WorkflowDTO>>({
    mutationFn: (patch) => {
      if (!id || !workflow) {
        throw new Error('Cannot update a workflow before it has loaded');
      }
      // The update is a full replace: merge only the defined patch fields over
      // the loaded entity (react-hook-form omits disabled inputs).
      const definedPatch = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined),
      ) as Partial<WorkflowDTO>;
      return automationGateway.updateWorkflow(id, { ...workflow, ...definedPatch });
    },
    linkedQuery: workflowQueryKey,
    successMessage: 'Updated workflow',
  });

  const { mutate: deleteWorkflow } = useGatewayMutation<void>({
    mutationFn: () => {
      if (!id) {
        throw new Error('Cannot delete a workflow before it has loaded');
      }
      return automationGateway.deleteWorkflow(id);
    },
    linkedQuery: ['workflows'],
    linkResponseToQuery: false,
    successMessage: 'Deleted workflow',
  });

  return { workflow, isLoading, updateWorkflow, deleteWorkflow };
}
